/**
 * IconThemeContext.jsx
 * React Context, Provider, and custom hook for Homy's icon theme engine.
 * Supports runtime theme variant switching ("spatial-bento-duotone", "spatial-bento-glow",
 * "spatial-bento-line", "obsidian-solid") and localStorage persistence.
 */

import * as React from "react";
import { domainMap, getDomainColor as getCanonicalDomainColor } from "./domainMap.js";

export const THEME_STORAGE_KEY = "homy-icon-theme-variant";

export const IconThemeContext = React.createContext({
  variant: "spatial-bento-duotone",
  setVariant: () => {},
  colorMode: "semantic",
  setColorMode: () => {},
  isGlowEnabled: false,
  toggleGlow: () => {},
  getDomainColor: getCanonicalDomainColor,
});

export function IconThemeProvider({
  defaultVariant = "spatial-bento-duotone",
  defaultColorMode = "semantic",
  children,
}) {
  const [variant, setVariantState] = React.useState(() => {
    if (typeof window !== "undefined") {
      try {
        const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
        if (
          stored &&
          [
            "spatial-bento-duotone",
            "spatial-bento-glow",
            "spatial-bento-line",
            "obsidian-solid",
          ].includes(stored)
        ) {
          return stored;
        }
      } catch (_e) {
        // Fallback gracefully on storage access errors
      }
    }
    return defaultVariant;
  });

  const [colorMode, setColorMode] = React.useState(defaultColorMode);
  const [isGlowEnabled, setIsGlowEnabled] = React.useState(
    variant === "spatial-bento-glow"
  );

  const setVariant = React.useCallback((nextVariant) => {
    setVariantState(nextVariant);
    if (nextVariant === "spatial-bento-glow") {
      setIsGlowEnabled(true);
    } else {
      setIsGlowEnabled(false);
    }
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, nextVariant);
      } catch (_e) {
        // Ignore localStorage write errors (e.g. Incognito)
      }
    }
  }, []);

  const toggleGlow = React.useCallback((force) => {
    setIsGlowEnabled((prev) => (force !== undefined ? force : !prev));
  }, []);

  const getDomainColor = React.useCallback((domain) => {
    return getCanonicalDomainColor(domain);
  }, []);

  const value = React.useMemo(
    () => ({
      variant,
      setVariant,
      colorMode,
      setColorMode,
      isGlowEnabled,
      toggleGlow,
      getDomainColor,
    }),
    [variant, setVariant, colorMode, isGlowEnabled, toggleGlow, getDomainColor]
  );

  return (
    <IconThemeContext.Provider value={value}>
      {children}
    </IconThemeContext.Provider>
  );
}

export function useIconTheme() {
  const context = React.useContext(IconThemeContext);
  return (
    context || {
      variant: "spatial-bento-duotone",
      setVariant: () => {},
      colorMode: "semantic",
      setColorMode: () => {},
      isGlowEnabled: false,
      toggleGlow: () => {},
      getDomainColor: getCanonicalDomainColor,
    }
  );
}
